import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { BookOpenCheck, BookOpenText, Brain, CalendarClock, Clock3, GraduationCap, HelpCircle, History, Layers3, LockOpen, LogOut, Map, Mic2, ShieldCheck, UserRound, Volume2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const SPEECH_RATE = 0.92;

const navItems = [
  { to: "/", label: "Hoje", icon: Clock3, testId: "nav-hoje" },
  { to: "/unlock", label: "Falar", icon: LockOpen, testId: "nav-falar" },
  { to: "/zero", label: "Inglês do Zero", icon: BookOpenCheck, testId: "nav-ingles-do-zero" },
  { to: "/modules", label: "Aulas", icon: Layers3, testId: "nav-aulas" },
  { to: "/live", label: "Ao Vivo", icon: CalendarClock, testId: "nav-ao-vivo" },
  { to: "/grammar", label: "Gramática", icon: GraduationCap, testId: "nav-grammar-link" },
  { to: "/review", label: "Revisar", icon: Brain, testId: "nav-review-link" },
  { to: "/scenarios", label: "Praticar", icon: Map, testId: "nav-scenarios-link" },
  { to: "/history", label: "Histórico", icon: History, testId: "nav-history-link" },
  { to: "/profile", label: "Perfil", icon: UserRound, testId: "nav-profile-link" },
];
const adminNavItem = { to: "/admin", label: "Admin", icon: ShieldCheck, testId: "nav-admin-link" };
const teacherGuideNavItem = { to: "/teacher-guide", label: "Guia do Professor", icon: BookOpenText, testId: "nav-teacher-guide-link" };

function AppNavigation({ handleLogout, user }) {
  return (
    <nav className="flex flex-wrap gap-2" data-testid="main-navigation">
      {[...navItems, ...(user?.role === "admin" ? [teacherGuideNavItem, adminNavItem] : [])].map((item) => {
        const Icon = item.icon;
        return (
          <NavLink key={item.to} to={item.to} end={item.to === "/"} data-testid={item.testId} className={({ isActive }) => `nav-pill ${isActive ? "nav-pill-active" : ""}`}>
            <Icon size={22} strokeWidth={2.5} />
            <span>{item.label}</span>
          </NavLink>
        );
      })}
      <button type="button" className="nav-pill" onClick={handleLogout} data-testid="logout-button">
        <LogOut size={22} strokeWidth={2.5} />
        <span>Sair</span>
      </button>
    </nav>
  );
}

function getCurrentInstruction(pathname) {
  if (pathname.includes("/lesson/")) return "Você está em uma simulação. Primeiro leia a mensagem. Depois digite ou grave sua resposta. Por último, clique em enviar resposta.";
  if (pathname.includes("/unlock")) return "Você está no modo falar. Clique em iniciar drill, responda a frase e depois clique em corrigir.";
  if (pathname.includes("/modules")) return "Você está em aulas. Procure a área seu exercício agora. Responda os exercícios e clique em corrigir exercícios.";
  if (pathname.includes("/zero")) return "Você está no inglês do zero. Procure a aula atual, responda os exercícios e clique em corrigir exercícios.";
  if (pathname.includes("/grammar")) return "Você está em gramática. Escolha a aula, responda os exercícios e clique em corrigir exercícios.";
  return "Comece pelo botão encontrar exercícios ou clique em falar para treinar uma resposta rápida.";
}

function FloatingHelpDock() {
  const navigate = useNavigate();
  const goToCurrentExercise = () => navigate("/modules", { state: { focusCurrentExercise: true } });
  const speakInstruction = () => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(getCurrentInstruction(window.location.pathname));
    utterance.lang = "pt-BR";
    utterance.rate = SPEECH_RATE;
    window.speechSynthesis.speak(utterance);
  };
  return (
    <div className="floating-help-dock" data-testid="floating-help-dock">
      <button type="button" className="floating-help-button" onClick={goToCurrentExercise} data-testid="floating-lost-help-button">
        <HelpCircle size={24} /> Estou perdido
      </button>
      <button type="button" className="floating-audio-button" onClick={speakInstruction} data-testid="floating-listen-instructions-button">
        <Volume2 size={22} /> Ouvir instrução
      </button>
    </div>
  );
}

export default function AppShell() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[var(--page-bg)] text-white easy-read-mode">
      <header className="app-header-shell sticky top-0 z-30 border-b border-white/15 bg-[#09090B]/92 backdrop-blur-xl" data-testid="app-header">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-home-link">
            <span className="flex h-12 w-12 items-center justify-center rounded-[8px] border border-white/20 bg-[#EAB308] text-[#09090B] shadow-[0_14px_34px_rgba(234,179,8,0.22)]" data-testid="brand-logo-mark">
              <Mic2 size={24} strokeWidth={2.6} />
            </span>
            <span className="min-w-0">
              <span className="block font-display text-xl font-black" data-testid="brand-name-text">Speak15 USA</span>
              <span className="block text-base font-bold uppercase text-white/65" data-testid="brand-tagline-text">simulador diário</span>
            </span>
          </Link>
          <div className="flex flex-col gap-3 lg:items-end" data-testid="header-actions-area">
            <AppNavigation handleLogout={handleLogout} user={user} />
            <p className="text-base font-black uppercase text-white/65" data-testid="header-user-summary">Aluno: {user?.name}</p>
          </div>
        </div>
      </header>
      <main className="app-main-content" data-testid="app-main-content"><Outlet /></main>
      <FloatingHelpDock />
    </div>
  );
}